import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Car,
  ChevronRight,
  ShieldCheck,
  Fuel,
  Gauge,
  MapPin,
  Send,
  Building2,
  Sparkles,
  ArrowRight,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { useMarketplaceListings, useCarBrands } from '@/modules/cars/hooks/use-cars'
import { toast } from 'sonner'

const MODEL_CATALOG_LOOKUP: Record<string, { brandName: string; modelName: string; bodyType: string }> = {
  'audi-a6': { brandName: 'Audi', modelName: 'A6', bodyType: 'Sedan' },
  'audi-rs7': { brandName: 'Audi', modelName: 'RS7 Sportback', bodyType: 'Coupe' },
  'audi-q7': { brandName: 'Audi', modelName: 'Q7', bodyType: 'SUV' },
  'bmw-3-series': { brandName: 'BMW', modelName: '3 Series', bodyType: 'Sedan' },
  'bmw-x5': { brandName: 'BMW', modelName: 'X5', bodyType: 'SUV' },
  'bmw-m4-competition': { brandName: 'BMW', modelName: 'M4 Competition', bodyType: 'Coupe' },
  'mercedes-benz-c-class': { brandName: 'Mercedes-Benz', modelName: 'C-Class', bodyType: 'Sedan' },
  'mercedes-benz-g-class': { brandName: 'Mercedes-Benz', modelName: 'G-Class (G63 AMG)', bodyType: 'SUV' },
  'porsche-911': { brandName: 'Porsche', modelName: '911 Carrera / GT3 RS', bodyType: 'Coupe' },
  'land-rover-defender': { brandName: 'Land Rover', modelName: 'Defender 110', bodyType: 'SUV' },
  'land-rover-range-rover-velar': { brandName: 'Land Rover', modelName: 'Range Rover Velar', bodyType: 'SUV' },
  'mahindra-thar': { brandName: 'Mahindra', modelName: 'Thar 4x4', bodyType: 'SUV' },
  'mahindra-xuv700': { brandName: 'Mahindra', modelName: 'XUV700', bodyType: 'SUV' },
  'mahindra-scorpio-n': { brandName: 'Mahindra', modelName: 'Scorpio-N', bodyType: 'SUV' },
  'toyota-fortuner': { brandName: 'Toyota', modelName: 'Fortuner Legender', bodyType: 'SUV' },
  'toyota-innova-hycross': { brandName: 'Toyota', modelName: 'Innova Hycross', bodyType: 'MUV' },
  'hyundai-creta': { brandName: 'Hyundai', modelName: 'Creta', bodyType: 'SUV' },
  'hyundai-verna': { brandName: 'Hyundai', modelName: 'Verna', bodyType: 'Sedan' },
  'volkswagen-virtus': { brandName: 'Volkswagen', modelName: 'Virtus GT', bodyType: 'Sedan' },
  'skoda-slavia': { brandName: 'Skoda', modelName: 'Slavia TSI', bodyType: 'Sedan' },
}

export function CarModelShowcasePage() {
  const { modelSlug } = useParams<{ modelSlug: string }>()
  const [inquiryName, setInquiryName] = useState('')
  const [inquiryPhone, setInquiryPhone] = useState('')
  const [inquiryMessage, setInquiryMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Fetch listings for this model
  const { data: marketplaceData, isLoading } = useMarketplaceListings({
    modelSlug: modelSlug || undefined,
    limit: 24,
  })

  const { data: dbBrands } = useCarBrands()
  const listings = marketplaceData?.items || []
  const firstListing = listings[0]

  // Resolve Model metadata dynamically from Database or Catalog lookup
  let resolvedBrandName = ''
  let resolvedModelName = ''
  let resolvedBodyType = 'Luxury Vehicle'

  if (dbBrands && dbBrands.length > 0) {
    for (const b of dbBrands) {
      const m = b.models?.find((mod: any) => mod.slug === modelSlug || modelSlug?.includes(mod.slug))
      if (m) {
        resolvedBrandName = b.name
        resolvedModelName = m.name
        resolvedBodyType = m.bodyType || 'Luxury Vehicle'
        break
      }
    }
  }

  if (!resolvedModelName && modelSlug && MODEL_CATALOG_LOOKUP[modelSlug]) {
    const meta = MODEL_CATALOG_LOOKUP[modelSlug]
    resolvedBrandName = meta.brandName
    resolvedModelName = meta.modelName
    resolvedBodyType = meta.bodyType
  }

  if (!resolvedModelName) {
    resolvedModelName = firstListing?.model?.name || modelSlug?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Vehicle Model'
    resolvedBrandName = firstListing?.brand?.name || resolvedModelName.split(' ')[0]
  }

  const fullTitle = `${resolvedBrandName} ${resolvedModelName}`.trim()

  // Calculate pricing range
  const prices = listings.map((l: any) => l.askingPrice).filter(Boolean)
  const minPrice = prices.length > 0 ? Math.min(...prices) : null
  const maxPrice = prices.length > 0 ? Math.max(...prices) : null

  const formatCurrency = (amount?: number | null) => {
    if (!amount) return 'Price On Call'
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} Lakh`
    return `₹${amount.toLocaleString('en-IN')}`
  }

  const handleInquirySubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!inquiryName || !inquiryPhone) {
      toast.error('Please enter your name and phone number')
      return
    }
    setIsSubmitting(true)
    setTimeout(() => {
      setIsSubmitting(false)
      toast.success(`Inquiry for ${fullTitle} submitted! A dealer representative will contact you shortly.`)
      setInquiryName('')
      setInquiryPhone('')
      setInquiryMessage('')
    }, 800)
  }

  return (
    <div className="min-h-screen space-y-12 pb-16">
      {/* HERO BANNER SECTION */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-900 via-slate-900 to-background text-white pt-12 pb-16 border-b border-border/40">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#CF2F2F]/20 via-transparent to-transparent opacity-60" />
        
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10 space-y-6">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-xs font-semibold text-slate-400">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="size-3" />
            <Link to="/cars" className="hover:text-white transition-colors">Marketplace</Link>
            <ChevronRight className="size-3" />
            <span className="text-[#CF2F2F] font-bold uppercase">{fullTitle}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center pt-2">
            <div className="lg:col-span-8 space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <Badge className="bg-[#CF2F2F] text-white hover:bg-[#CF2F2F] px-3 py-1 font-bold text-xs uppercase tracking-wider">
                  <Sparkles className="size-3.5 mr-1" /> Official Model Showcase
                </Badge>
                <Badge variant="outline" className="text-slate-300 border-slate-700 bg-slate-800/60 uppercase font-bold text-xs">
                  {resolvedBodyType}
                </Badge>
                <Badge variant="outline" className="text-emerald-400 border-emerald-500/30 bg-emerald-500/10 font-bold text-xs">
                  <ShieldCheck className="size-3.5 mr-1" /> Verified Dealer Listings
                </Badge>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight text-white font-display">
                {fullTitle}
              </h1>

              <p className="text-slate-300 text-sm sm:text-base max-w-2xl font-sans leading-relaxed">
                Compare prices and explore verified pre-owned & new <strong className="text-white">{fullTitle}</strong> cars listed by authorized dealers and private luxury sellers across India.
              </p>

              {/* Price & Specs Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4">
                <div className="bg-slate-800/80 backdrop-blur-md rounded-xl p-3.5 border border-slate-700/60">
                  <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Starting Price</span>
                  <span className="text-lg font-extrabold text-[#CF2F2F]">{minPrice ? formatCurrency(minPrice) : 'N/A'}</span>
                </div>
                <div className="bg-slate-800/80 backdrop-blur-md rounded-xl p-3.5 border border-slate-700/60">
                  <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Max Listing Price</span>
                  <span className="text-lg font-extrabold text-white">{maxPrice ? formatCurrency(maxPrice) : 'N/A'}</span>
                </div>
                <div className="bg-slate-800/80 backdrop-blur-md rounded-xl p-3.5 border border-slate-700/60">
                  <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Available Units</span>
                  <span className="text-lg font-extrabold text-white">{listings.length} Listed</span>
                </div>
                <div className="bg-slate-800/80 backdrop-blur-md rounded-xl p-3.5 border border-slate-700/60">
                  <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Brand Lineup</span>
                  <span className="text-lg font-extrabold text-white uppercase">{resolvedBrandName}</span>
                </div>
              </div>
            </div>

            {/* Quick Hero Image Preview */}
            <div className="lg:col-span-4 flex justify-center">
              <div className="relative w-full max-w-sm rounded-2xl overflow-hidden border border-slate-700/60 shadow-2xl bg-slate-800/40 p-2">
                {firstListing?.images?.[0]?.url ? (
                  <img
                    src={firstListing.images[0].url}
                    alt={fullTitle}
                    className="w-full h-56 object-cover rounded-xl transition-transform duration-500 hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-56 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50 flex flex-col items-center justify-center p-6 text-center space-y-2">
                    <Car className="size-16 text-[#CF2F2F]/80" />
                    <span className="text-sm font-extrabold text-white uppercase">{fullTitle}</span>
                    <span className="text-[11px] text-slate-400 font-semibold">{resolvedBodyType}</span>
                  </div>
                )}
                <div className="p-3 text-center">
                  <span className="text-xs font-bold text-slate-300 block">{fullTitle} Official Showcase</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DEALER INVENTORY & LISTINGS GRID SECTION */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-4">
          <div>
            <h2 className="text-2xl font-extrabold uppercase tracking-tight text-foreground font-display flex items-center gap-2">
              <Car className="size-6 text-[#CF2F2F]" /> Available {fullTitle} Cars ({listings.length})
            </h2>
            <p className="text-xs text-muted-foreground">
              Compare prices, mileage, and seller details from multiple dealerships.
            </p>
          </div>
          <Link to="/cars">
            <Button variant="outline" size="sm">
              Browse All Cars <ArrowRight className="size-4 ml-1" />
            </Button>
          </Link>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <div key={n} className="h-80 rounded-2xl bg-border/40 animate-pulse" />
            ))}
          </div>
        ) : listings.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map((car: any) => (
              <motion.div
                key={car.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="group rounded-2xl border border-border bg-card overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col"
              >
                {/* Car Image Header */}
                <div className="relative h-48 overflow-hidden bg-slate-900">
                  <img
                    src={car.images?.[0]?.url || '/images/hero-car.png'}
                    alt={car.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-md px-2.5 py-1 rounded-full text-white font-extrabold text-xs">
                    {formatCurrency(car.askingPrice)}
                  </div>
                  <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-md px-2.5 py-0.5 rounded-md text-white text-[11px] font-bold">
                    {car.manufactureYear}
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 space-y-3 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="text-base font-bold text-foreground line-clamp-1 group-hover:text-[#CF2F2F] transition-colors">
                      {car.title}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                      <MapPin className="size-3.5 text-[#CF2F2F]" />
                      <span>{car.locationCity || 'India'}</span>
                      <span>•</span>
                      <span>{car.mileageKm ? `${car.mileageKm.toLocaleString('en-IN')} km` : 'Unused'}</span>
                    </div>
                  </div>

                  {/* Specs Pill bar */}
                  <div className="grid grid-cols-2 gap-2 text-[11px] font-semibold bg-muted/40 p-2 rounded-lg">
                    <div className="flex items-center gap-1.5 text-foreground/80">
                      <Fuel className="size-3.5 text-amber-500" />
                      <span>{car.fuelType || 'Petrol'}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-foreground/80">
                      <Gauge className="size-3.5 text-blue-500" />
                      <span>{car.transmission || 'Automatic'}</span>
                    </div>
                  </div>

                  {/* Seller / Dealer Info */}
                  <div className="pt-2 border-t border-border/60 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs font-semibold text-foreground/80">
                      <Building2 className="size-4 text-[#CF2F2F]" />
                      <span className="truncate max-w-[120px]">{car.user?.name || 'Verified Merchant'}</span>
                    </div>

                    <Link to={`/cars/${car.slug}`}>
                      <Button size="sm" className="bg-[#CF2F2F] hover:bg-[#CF2F2F]/90 text-white font-bold">
                        Inspect <ChevronRight className="size-3.5 ml-0.5" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-muted/20 rounded-2xl border border-dashed border-border space-y-4">
            <Car className="size-12 mx-auto text-muted-foreground opacity-50" />
            <h3 className="text-lg font-bold text-foreground">No active listings for {fullTitle} right now</h3>
            <p className="text-xs text-muted-foreground max-w-md mx-auto">
              Be the first seller to list a {fullTitle} or submit an inquiry below to get notified when dealers post new inventory.
            </p>
          </div>
        )}
      </section>

      {/* QUICK INQUIRY FORM */}
      <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-slate-200 dark:border-border bg-white dark:bg-card p-6 sm:p-8 shadow-xl space-y-6">
          <div className="text-center space-y-2">
            <Badge className="bg-[#CF2F2F]/10 text-[#CF2F2F] border-none font-bold">
              DIRECT DEALER INQUIRY
            </Badge>
            <h3 className="text-2xl font-extrabold uppercase tracking-tight text-foreground font-display">
              Interested in buying a {fullTitle}?
            </h3>
            <p className="text-xs text-muted-foreground max-w-md mx-auto">
              Submit your request and get official dealer quotes & test drive availability delivered directly to you.
            </p>
          </div>

          <form onSubmit={handleInquirySubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Full Name *</label>
              <Input
                placeholder="Enter your name"
                value={inquiryName}
                onChange={(e) => setInquiryName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Phone Number *</label>
              <Input
                placeholder="+91 98765 43210"
                value={inquiryPhone}
                onChange={(e) => setInquiryPhone(e.target.value)}
                required
              />
            </div>

            <div className="sm:col-span-2 space-y-1.5">
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Message / Preferred Variant</label>
              <Input
                placeholder={`I am interested in buying a ${fullTitle}...`}
                value={inquiryMessage}
                onChange={(e) => setInquiryMessage(e.target.value)}
              />
            </div>

            <div className="sm:col-span-2 pt-2">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#CF2F2F] hover:bg-[#CF2F2F]/90 text-white font-extrabold py-6 rounded-xl shadow-lg"
              >
                {isSubmitting ? (
                  'Submitting Request...'
                ) : (
                  <>
                    <Send className="size-4 mr-2" /> Send Inquiry to Certified Dealers
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  )
}
